import React, { useState } from 'react';

import {
  ActionIcon,
  Button,
  Card,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { IconArrowLeft, IconSettings } from '@tabler/icons-react';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'context/auth-context';
import useCommittedClubProgram from 'hooks/committedClub/useCommittedClubProgram';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PROGRAM_NAME } from 'utils/checkInProgram';

import SettingsDrawer from './components/SettingsDrawer';

const CommittedClubSettings = (): JSX.Element => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { hasPerm } = useAuth();
  const { data, isLoading } = useCommittedClubProgram();
  const [drawerOpened, setDrawerOpened] = useState(false);

  const program = data?.data.results?.[0];

  return (
    <>
      <PageHeader>
        <Group position='apart' align='center'>
          <Group spacing='sm' align='flex-start'>
            <ActionIcon
              variant='subtle'
              onClick={() => navigate('/')}
              aria-label='Back to dashboard'
              style={{ alignSelf: 'center' }}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>

            <Stack spacing={0}>
              <Title order={3}>{program?.name ?? DEFAULT_PROGRAM_NAME}</Title>
              <Text color='dimmed' size='sm'>
                Monitor monthly check-in progress
              </Text>
            </Stack>
          </Group>
          <Button
            variant='default'
            leftIcon={<IconSettings size={16} />}
            onClick={() => setDrawerOpened(true)}
          >
            Settings
          </Button>
        </Group>
      </PageHeader>

      <div style={{ position: 'relative', minHeight: 240 }}>
        <LoadingOverlay visible={isLoading} />

        {!isLoading && !program && (
          <Center mt={20} style={{ minHeight: 240 }}>
            <Card
              withBorder
              radius='md'
              padding='xl'
              bg={theme.colors.gray[0]}
              style={{ maxWidth: 640, width: '100%' }}
            >
              <Stack align='center' spacing='md'>
                <ThemeIcon size={64} radius='xl' variant='light' color='gray'>
                  <IconSettings size={32} />
                </ThemeIcon>
                <Title order={4} align='center'>
                  Set up your {DEFAULT_PROGRAM_NAME} program to start tracking check-ins
                </Title>
                <Text color='dimmed' align='center'>
                  Create your {DEFAULT_PROGRAM_NAME} program to start tracking athlete progress and
                  celebrating their commitment.
                </Text>
                <Button leftIcon={<IconSettings size={16} />} onClick={() => setDrawerOpened(true)}>
                  Go to Settings
                </Button>
              </Stack>
            </Card>
          </Center>
        )}

        {!isLoading && program && (
          <Card withBorder radius='md' padding='lg' style={{ maxWidth: 640 }}>
            <Stack spacing='xs'>
              <Title order={4}>{program.name}</Title>
              <Text color='dimmed'>{program.currentThreshold} check-ins required per month</Text>
            </Stack>
          </Card>
        )}
      </div>

      <SettingsDrawer
        opened={drawerOpened}
        closeHandler={() => setDrawerOpened(false)}
        program={program}
        permission={hasPerm('CheckInProgramManage')}
      />
    </>
  );
};

export default CommittedClubSettings;
